import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ARTIFACT_PANEL_ID, useArtifactActions, useArtifactState } from './ArtifactContext';
import { getArtifactRenderer } from './registry';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { DOCKED_PANEL_QUERY, useMediaQuery } from '../../hooks/useMediaQuery';
import { readPref, writePref } from '../../utils/storage';
import { AlertIcon, CloseIcon, MaximizeIcon, MinimizeIcon } from '../icons';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { panelBadge } from '../visuals/typeBadge';
import './ArtifactPanel.css';

const WIDTH_KEY = 'digitrans_artifact_width';
const MIN_W = 380;
const MAX_W = 780;
// La conversation garde au moins cette largeur à côté du panneau
const CHAT_MIN_W = 360;
const KEY_STEP = 24;

const clampWidth = (width) => {
  const max = Math.min(MAX_W, Math.max(MIN_W, window.innerWidth - CHAT_MIN_W));
  return Math.round(Math.min(max, Math.max(MIN_W, width)));
};

/**
 * Panneau d'artefacts à droite (UI-03).
 * - Écran large : panneau ancré (role complementary), 46 % de la largeur entre 380 et
 *   780 px, redimensionnable (glisser ou flèches sur la poignée), sans piège de focus.
 * - Plein écran, ou écran étroit (< 1024 px) : fenêtre modale (role dialog), piège de focus.
 * - Échap ferme ; le focus revient au bouton qui a ouvert le panneau.
 */
export function ArtifactPanel({ messages }) {
  const { artifact, fullscreen, triggerRef } = useArtifactState();
  const { close, setFullscreen } = useArtifactActions();
  const wide = useMediaQuery(DOCKED_PANEL_QUERY);
  const reduceMotion = useReducedMotion();
  const modal = !wide || fullscreen;
  const titleId = useId();

  const rootRef = useRef(null);
  const titleRef = useRef(null);
  const [width, setWidth] = useState(() => {
    const saved = readPref(WIDTH_KEY, null);
    return typeof saved === 'number' ? saved : null;
  });
  const [resizing, setResizing] = useState(false);

  useFocusTrap(rootRef, !!artifact && modal);

  // Nouvel artefact : le focus passe sur son titre (annoncé par les lecteurs d'écran)
  const artifactId = artifact?.id ?? null;
  // Ouverture automatique (carte live pendant une réponse) : le focus reste dans le compositeur
  const silencieux = artifact?.silencieux === true;
  useEffect(() => {
    if (!artifactId || silencieux) return;
    const frame = requestAnimationFrame(() => titleRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [artifactId, silencieux]);

  const closePanel = useCallback(() => {
    const trigger = triggerRef.current;
    close();
    // Retour du focus au déclencheur s'il est toujours affiché, sinon à la conversation
    requestAnimationFrame(() => {
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      else document.querySelector('main')?.focus?.({ preventScroll: true });
    });
  }, [close, triggerRef]);

  // Échap : ferme le panneau (fenêtre modale, ou focus à l'intérieur du panneau ancré)
  useEffect(() => {
    if (!artifact) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (!modal && !rootRef.current?.contains(document.activeElement)) return;
      event.preventDefault();
      if (fullscreen && wide) setFullscreen(false);
      else closePanel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [artifact, modal, fullscreen, wide, closePanel, setFullscreen]);

  // Fenêtre modale : la page derrière ne défile plus
  useEffect(() => {
    if (!artifact || !modal) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [artifact, modal]);

  // ── Redimensionnement (poignée à gauche du panneau ancré) ──
  const commitWidth = (next) => {
    setWidth(next);
    writePref(WIDTH_KEY, next);
  };

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture?.(event.pointerId);
    setResizing(true);
    let latest = width;
    const onMove = (e) => {
      latest = clampWidth(window.innerWidth - e.clientX);
      setWidth(latest);
    };
    const onUp = () => {
      setResizing(false);
      if (latest) writePref(WIDTH_KEY, latest);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  };

  const currentWidth = () => width ?? rootRef.current?.getBoundingClientRect().width ?? MIN_W;
  const onHandleKeyDown = (event) => {
    let next = null;
    if (event.key === 'ArrowLeft') next = currentWidth() + KEY_STEP;
    if (event.key === 'ArrowRight') next = currentWidth() - KEY_STEP;
    if (event.key === 'Home') next = MIN_W;
    if (event.key === 'End') next = MAX_W;
    if (next === null) return;
    event.preventDefault();
    commitWidth(clampWidth(next));
  };

  const renderer = artifact ? getArtifactRenderer(artifact.type) : null;
  // Badge du type : teinte et icône du visuel affiché, de la carte, du format du document… ;
  // sinon libellé et icône du registre, ton primaire
  const badge = (artifact && panelBadge(artifact)) ?? { type: true, icon: renderer?.Icon ?? AlertIcon, label: renderer?.label ?? 'Artefact' };
  const Viewer = renderer?.Component ?? null;

  const slide = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : { initial: { opacity: 0, x: 32 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 32 } };

  return (
    <AnimatePresence>
      {artifact && (
        <motion.section
          key="artifact-panel"
          ref={rootRef}
          id={ARTIFACT_PANEL_ID}
          className={`artifact-panel ${modal ? 'is-modal' : 'is-docked'} ${fullscreen ? 'is-fullscreen' : ''} ${resizing ? 'is-resizing' : ''}`}
          role={modal ? 'dialog' : 'complementary'}
          aria-modal={modal ? 'true' : undefined}
          aria-labelledby={titleId}
          tabIndex={-1}
          style={!modal && width ? { '--artifact-w': `${clampWidth(width)}px` } : undefined}
          {...slide}
          transition={{ duration: reduceMotion ? 0.14 : 0.36, ease: [0.2, 0, 0, 1] }}
        >
          {!modal && (
            <div
              className="artifact-panel__resize"
              role="separator"
              aria-orientation="vertical"
              aria-label="Redimensionner le panneau"
              aria-valuemin={MIN_W}
              aria-valuemax={MAX_W}
              aria-valuenow={Math.round(width ?? MIN_W)}
              tabIndex={0}
              title="Glisser pour redimensionner (double-clic : largeur par défaut)"
              onPointerDown={onPointerDown}
              onKeyDown={onHandleKeyDown}
              onDoubleClick={() => { setWidth(null); writePref(WIDTH_KEY, null); }}
            />
          )}

          <header className="artifact-panel__header">
            <Badge type={badge.type} icon={badge.icon} className="artifact-panel__type">{badge.label}</Badge>
            <div className="artifact-panel__titles">
              <h2 id={titleId} ref={titleRef} tabIndex={-1} className="artifact-panel__title">
                {artifact.title || renderer?.label || 'Artefact'}
              </h2>
              {artifact.subtitle && <p className="artifact-panel__subtitle ds-num">{artifact.subtitle}</p>}
            </div>
            <div className="artifact-panel__actions">
              {wide && (
                <Button
                  variant="ghost"
                  icon={fullscreen ? MinimizeIcon : MaximizeIcon}
                  iconOnly
                  className="artifact-icon-button"
                  aria-pressed={fullscreen}
                  aria-label={fullscreen ? 'Quitter le plein écran' : 'Plein écran'}
                  title={fullscreen ? 'Quitter le plein écran' : 'Plein écran'}
                  onClick={() => setFullscreen(!fullscreen)}
                />
              )}
              <Button
                variant="ghost"
                icon={CloseIcon}
                iconOnly
                className="artifact-icon-button"
                aria-label="Fermer le panneau (Échap)"
                title="Fermer (Échap)"
                onClick={closePanel}
              />
            </div>
          </header>

          <div className="artifact-panel__body">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={artifact.id}
                className="artifact-panel__content"
                initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduceMotion ? 0 : -4 }}
                transition={{ duration: reduceMotion ? 0.1 : 0.22, ease: [0, 0, 0, 1] }}
              >
                {Viewer ? (
                  <Viewer artifact={artifact} messages={renderer.needsMessages ? messages : undefined} />
                ) : (
                  <p className="artifact-empty">Ce type de contenu ne peut pas encore être affiché ici.</p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
}
